import { z } from "zod";
import { narrativeBookProductSchema } from "./narrative-book-production.js";
export const narrativeCompositionSurfaceSchema = z.enum([
    "COMPLETE_PDF",
    "REPRESENTATIVE_SPREAD",
    "PRINT_INTERIOR",
    "BROWSER_PREVIEW",
    "REVIEW_SAMPLE",
]);
export const narrativeCompositionRoleSchema = z.enum([
    "FRONTMATTER",
    "SCENE",
    "REFLECTION",
    "TRANSITION",
    "CHRONOLOGY",
    "CORRECTION",
    "CORRESPONDENCE",
    "PHOTOGRAPH",
    "DOCUMENT",
    "OBJECT",
    "MAP",
    "TIMELINE",
    "QUOTATION",
    "LEGACY",
    "QUIET_CODA",
    "INDIVIDUAL_SCENE",
    "FACING_RESPONSE",
    "ALTERNATING_VOICES",
    "DIFFERING_RECOLLECTIONS",
    "SHARED_NARRATIVE",
    "WITNESS_EVIDENCE",
    "PAIRED_CAPTIONS",
    "JOINT_REFLECTION",
    "UNRESOLVED_MOMENT",
    "ENDMATTER",
    "INTENTIONAL_BLANK",
]);
export const narrativeEmotionalFunctionSchema = z.enum([
    "ORIENT",
    "ENTER_SCENE",
    "DEEPEN",
    "TURN",
    "CORRECT",
    "COMPARE",
    "PAUSE",
    "WITNESS",
    "CONNECT",
    "RESOLVE_WITHOUT_ERASING",
    "HAND_FORWARD",
    "REST",
]);
export const narrativeEvidenceTypeSchema = z.enum([
    "MEMORY",
    "CORRECTED_MEMORY",
    "CHRONOLOGY",
    "LETTER",
    "PHOTOGRAPH",
    "DOCUMENT",
    "OBJECT",
    "MAP",
    "TIMELINE",
    "WITNESS",
    "DIFFERING_MEMORY",
    "JOINTLY_APPROVED_MEMORY",
    "UNRESOLVED_MEMORY",
    "NONE",
]);
export const narrativeTextLengthSchema = z.enum(["NONE", "SHORT", "MEDIUM", "LONG"]);
export const narrativeImageAvailabilitySchema = z.enum([
    "NONE",
    "SYNTHETIC_PROOF_ONLY",
    "APPROVED_ORIGINAL",
]);
export const narrativeImageGeometrySchema = z.enum([
    "NONE",
    "PORTRAIT",
    "LANDSCAPE",
    "SQUARE",
    "PANORAMIC",
    "DOCUMENT_PORTRAIT",
    "DOCUMENT_LANDSCAPE",
    "PAIR",
]);
export const narrativePageTurnImportanceSchema = z.enum(["LOW", "MEDIUM", "HIGH", "CRITICAL"]);
export const narrativeContributorSchema = z.enum(["MAYA", "ROWAN"]);
export const narrativeCompositionMasterSchema = z
    .object({
    masterId: z.string().trim().min(1).max(160),
    product: narrativeBookProductSchema,
    version: z.string().trim().min(1).max(80),
    scope: z.enum(["PAGE", "SPREAD"]),
    role: narrativeCompositionRoleSchema,
    topology: z.string().trim().min(10).max(500),
    supportedEvidenceTypes: z.array(narrativeEvidenceTypeSchema).min(1),
    supportedTextLengths: z.array(narrativeTextLengthSchema).min(1),
    supportedImageStates: z.array(narrativeImageAvailabilitySchema).min(1),
    supportedImageGeometries: z.array(narrativeImageGeometrySchema).min(1),
    minimumPageTurnImportance: narrativePageTurnImportanceSchema,
    surfaces: z.array(narrativeCompositionSurfaceSchema).length(5),
    contributorPolicy: z.enum([
        "NOT_APPLICABLE",
        "ALTERNATE_LEAD",
        "EQUAL_FACING_WEIGHT",
        "SHARED_EQUAL_OR_SUBORDINATE",
    ]),
})
    .strict();
export const narrativeCompositionBeatSchema = z
    .object({
    beatId: z.string().trim().min(1).max(200),
    position: z.number().int().nonnegative(),
    interiorPage: z.number().int().positive().nullable(),
    chapterId: z.string().trim().min(1).max(200).nullable(),
    bodyPageIndex: z.number().int().nonnegative().nullable(),
    role: narrativeCompositionRoleSchema,
    emotionalFunction: narrativeEmotionalFunctionSchema,
    evidenceType: narrativeEvidenceTypeSchema,
    textLength: narrativeTextLengthSchema,
    imageAvailability: narrativeImageAvailabilitySchema,
    imageGeometry: narrativeImageGeometrySchema,
    pageTurnImportance: narrativePageTurnImportanceSchema,
    masterId: z.string().trim().min(1).max(160),
    sourceParagraphIds: z.array(z.string().trim().min(1).max(200)).max(100),
    sourceAssetIds: z.array(z.string().trim().min(1).max(200)).max(20),
    contributorOrder: z.tuple([narrativeContributorSchema, narrativeContributorSchema]).nullable(),
    sharedVoiceProminence: z.enum(["NONE", "SUBORDINATE", "EQUAL"]),
    selectionReasons: z.array(z.string().trim().min(5).max(300)).min(2).max(10),
})
    .strict()
    .superRefine((value, context) => {
    if (value.contributorOrder && value.contributorOrder[0] === value.contributorOrder[1])
        context.addIssue({
            code: "custom",
            path: ["contributorOrder"],
            message: "A dual-voice beat must assign the two positions to different contributors.",
        });
});
export const narrativeCompositionPlanSchema = z
    .object({
    contract: z.literal("narrative-composition-plan/1"),
    product: narrativeBookProductSchema,
    plannerVersion: z.literal("SEMANTIC_BEAT_PLANNER_V1"),
    masterRegistryVersion: z.string().trim().min(1).max(80),
    masterRegistryChecksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    inputs: z
        .object({
        narrativeRole: z.literal(true),
        emotionalFunction: z.literal(true),
        evidenceType: z.literal(true),
        textLength: z.literal(true),
        imageAvailability: z.literal(true),
        imageGeometry: z.literal(true),
        pageTurnImportance: z.literal(true),
    })
        .strict(),
    masters: z.array(narrativeCompositionMasterSchema).min(10),
    beats: z.array(narrativeCompositionBeatSchema).min(1),
    chapterStarts: z.array(z
        .object({
        chapterId: z.string().trim().min(1).max(200),
        title: z.string().trim().min(1).max(200),
        interiorPage: z.number().int().positive(),
    })
        .strict()),
    intentionalBlankPages: z.array(z.number().int().positive()),
    interiorPageCount: z.number().int().positive(),
    sourceParagraphIds: z.array(z.string().trim().min(1).max(200)).min(1),
    allocatedParagraphIds: z.array(z.string().trim().min(1).max(200)).min(1),
    surfaces: z
        .object({
        completePdf: z.literal(true),
        representativeSpreads: z.literal(true),
        browserPreview: z.literal(true),
        printInterior: z.literal(true),
        reviewSamples: z.literal(true),
    })
        .strict(),
    contributorBalance: z
        .object({
        mayaLeads: z.number().int().nonnegative(),
        rowanLeads: z.number().int().nonnegative(),
        mayaLeftPositions: z.number().int().nonnegative(),
        rowanLeftPositions: z.number().int().nonnegative(),
        sharedDominantBeats: z.number().int().nonnegative(),
        passed: z.boolean(),
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((value, context) => {
    const beatIds = value.beats.map(({ beatId }) => beatId);
    if (new Set(beatIds).size !== beatIds.length)
        context.addIssue({ code: "custom", path: ["beats"], message: "Beat IDs must be unique." });
    const masterIds = new Set(value.masters.map(({ masterId }) => masterId));
    if (value.masters.some(({ product }) => product !== value.product))
        context.addIssue({
            code: "custom",
            path: ["masters"],
            message: "Every canonical master must belong to the composition plan product.",
        });
    value.beats.forEach((beat, index) => {
        if (!masterIds.has(beat.masterId))
            context.addIssue({
                code: "custom",
                path: ["beats", index, "masterId"],
                message: "Every beat must resolve to a canonical master in this registry.",
            });
    });
    if (value.sourceParagraphIds.join("|") !== value.allocatedParagraphIds.join("|"))
        context.addIssue({
            code: "custom",
            path: ["allocatedParagraphIds"],
            message: "Semantic beat allocation must preserve every paragraph exactly once and in order.",
        });
    if (value.beats.some(({ position }, index) => position !== index))
        context.addIssue({
            code: "custom",
            path: ["beats"],
            message: "Whole-book beats must be stored in their exact sequential position.",
        });
    const composedPages = value.beats.flatMap(({ interiorPage }) => interiorPage === null ? [] : [interiorPage]);
    if (new Set(composedPages).size !== composedPages.length)
        context.addIssue({
            code: "custom",
            path: ["beats"],
            message: "Only one canonical page beat may own an interior page.",
        });
    if (composedPages.some((page) => page > value.interiorPageCount))
        context.addIssue({
            code: "custom",
            path: ["interiorPageCount"],
            message: "No composition beat may exceed the declared interior page count.",
        });
    if (value.product === "MY_STORY" && value.contributorBalance !== null)
        context.addIssue({
            code: "custom",
            path: ["contributorBalance"],
            message: "My Story does not use a dual-contributor balance record.",
        });
    if (value.product === "OUR_STORY") {
        const balance = value.contributorBalance;
        if (!balance?.passed ||
            balance.sharedDominantBeats !== 0 ||
            Math.abs(balance.mayaLeads - balance.rowanLeads) > 1 ||
            Math.abs(balance.mayaLeftPositions - balance.rowanLeftPositions) > 1)
            context.addIssue({
                code: "custom",
                path: ["contributorBalance"],
                message: `Our Story requires alternating contributor leadership, balanced left positions, and no dominant shared beat (Maya leads ${balance?.mayaLeads ?? "missing"}, Rowan leads ${balance?.rowanLeads ?? "missing"}, Maya left ${balance?.mayaLeftPositions ?? "missing"}, Rowan left ${balance?.rowanLeftPositions ?? "missing"}, shared dominant ${balance?.sharedDominantBeats ?? "missing"}).`,
            });
    }
});
export const narrativeCompositionSpeakerSchema = z.enum([
    "NELL",
    "MAYA",
    "ROWAN",
    "JOINT",
    "WITNESS",
    "EDITORIAL",
]);
export const narrativeCompositionMediaInputSchema = z
    .object({
    assetId: z.string().trim().min(1).max(200),
    availability: narrativeImageAvailabilitySchema,
    geometry: narrativeImageGeometrySchema,
    mimeType: z.enum(["image/jpeg", "image/png", "image/tiff", "application/pdf"]).nullable(),
    pixelWidth: z.number().int().positive().nullable(),
    pixelHeight: z.number().int().positive().nullable(),
    focalPoint: z
        .object({ x: z.number().min(0).max(1), y: z.number().min(0).max(1) })
        .strict()
        .nullable(),
    cropApproved: z.boolean(),
    caption: z.string().max(4_000),
    reverseCaption: z.string().max(4_000).nullable(),
    transcription: z.string().max(20_000).nullable(),
    approvalState: z.enum(["PROOF_ONLY", "PENDING", "APPROVED", "EXCLUDED"]),
})
    .strict()
    .superRefine((value, context) => {
    if (value.availability === "NONE" && value.geometry !== "NONE")
        context.addIssue({
            code: "custom",
            path: ["geometry"],
            message: "Absent media must use NONE geometry.",
        });
    if (value.approvalState === "APPROVED" && value.availability !== "APPROVED_ORIGINAL")
        context.addIssue({
            code: "custom",
            path: ["approvalState"],
            message: "Only an approved original may have APPROVED state.",
        });
});
export const narrativeCompositionBlockSchema = z
    .object({
    blockId: z.string().trim().min(1).max(200),
    chapterId: z.string().trim().min(1).max(200).nullable(),
    order: z.number().int().nonnegative(),
    role: narrativeCompositionRoleSchema,
    emotionalFunction: narrativeEmotionalFunctionSchema,
    evidenceType: narrativeEvidenceTypeSchema,
    speaker: narrativeCompositionSpeakerSchema,
    text: z.string().max(100_000),
    sourceParagraphIds: z.array(z.string().trim().min(1).max(200)).max(500),
    sourceClaimIds: z.array(z.string().trim().min(1).max(200)).max(500),
    sourceAssetIds: z.array(z.string().trim().min(1).max(200)).max(50),
    uncertainDate: z.boolean(),
    ambiguousIdentity: z.boolean(),
    pageTurnImportance: narrativePageTurnImportanceSchema,
    media: narrativeCompositionMediaInputSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.speaker === "JOINT" && value.evidenceType !== "JOINTLY_APPROVED_MEMORY")
        context.addIssue({
            code: "custom",
            path: ["evidenceType"],
            message: "Joint voice requires jointly approved evidence.",
        });
    if (value.media && !value.sourceAssetIds.includes(value.media.assetId))
        context.addIssue({
            code: "custom",
            path: ["sourceAssetIds"],
            message: "A media block must bind its media asset identifier.",
        });
});
export const narrativeCompositionPolicySchema = z
    .object({
    contract: z.literal("narrative-composition-policy/1"),
    product: narrativeBookProductSchema,
    version: z.string().trim().min(1).max(100),
    contentRanges: z
        .object({
        preferredWords: z.tuple([z.number().int().positive(), z.number().int().positive()]),
        interiorPages: z.tuple([z.number().int().positive(), z.number().int().positive()]),
        approvedMedia: z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
        chapters: z.tuple([z.number().int().positive(), z.number().int().positive()]),
    })
        .strict(),
    text: z
        .object({
        bodyMinimumPoints: z.number().positive(),
        secondaryMinimumPoints: z.number().positive(),
        maximumConsecutiveProsePages: z.number().int().positive(),
        oversizedParagraphAction: z.literal("SPLIT_AT_SENTENCE_BOUNDARY_WITH_STABLE_FRAGMENT_IDS"),
        longCaptionAction: z.literal("CONTINUE_ON_CAPTION_PAGE"),
    })
        .strict(),
    media: z
        .object({
        minimumEffectiveDpi: z.number().int().positive(),
        unapprovedCropAction: z.literal("CONTAIN"),
        lowResolutionAction: z.literal("PROOF_PLACEHOLDER_AND_BLOCK_RELEASE"),
        documentSequence: z.tuple([
            z.literal("FRONT"),
            z.literal("REVERSE"),
            z.literal("TRANSCRIPTION"),
        ]),
    })
        .strict(),
    compatibility: z
        .array(z
        .object({
        masterId: z.string().trim().min(1).max(160),
        roles: z.array(narrativeCompositionRoleSchema).min(1),
        evidenceTypes: z.array(narrativeEvidenceTypeSchema).min(1),
        imageStates: z.array(narrativeImageAvailabilitySchema).min(1),
        imageGeometries: z.array(narrativeImageGeometrySchema).min(1),
    })
        .strict())
        .min(10),
})
    .strict()
    .superRefine((value, context) => {
    for (const [field, range] of Object.entries(value.contentRanges))
        if (range[0] > range[1])
            context.addIssue({
                code: "custom",
                path: ["contentRanges", field],
                message: "A supported range must be ordered minimum to maximum.",
            });
    const ids = value.compatibility.map(({ masterId }) => masterId);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["compatibility"],
            message: "Every compatibility entry must name a unique master.",
        });
});
export const narrativeCompositionInputSchema = z
    .object({
    contract: z.literal("narrative-composition-input/1"),
    product: narrativeBookProductSchema,
    policyVersion: z.string().trim().min(1).max(100),
    blocks: z.array(narrativeCompositionBlockSchema).min(1),
})
    .strict()
    .superRefine((value, context) => {
    if (value.blocks.some(({ order }, index) => order !== index))
        context.addIssue({
            code: "custom",
            path: ["blocks"],
            message: "Composition blocks must remain in exact source order.",
        });
    const ids = value.blocks.map(({ blockId }) => blockId);
    if (new Set(ids).size !== ids.length)
        context.addIssue({
            code: "custom",
            path: ["blocks"],
            message: "Composition block IDs must be unique.",
        });
});
export const narrativeHumanReviewRecordSchema = z
    .object({
    contract: z.literal("narrative-human-review/1"),
    product: narrativeBookProductSchema,
    pdfChecksumSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    reviewerId: z.string().trim().min(1).max(200),
    reviewedAt: z.string().datetime(),
    decision: z.enum(["APPROVED", "CHANGES_REQUESTED"]),
    notes: z.array(z.string().trim().min(1).max(1_000)).max(100),
})
    .strict();
//# sourceMappingURL=narrative-composition.js.map